import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {DirectusService} from '@frontend/directus';
import {BaseDialogComponent} from '../../../../shared/components/dialog/base-dialog/base-dialog.component';
import {ModelFieldPercentageComponent} from '../../../../shared/components/inputs/model-field/model-field-percentage/model-field-percentage.component';
import {ModelFieldNumberComponent} from '../../../../shared/components/inputs/model-field/model-field-number/model-field-number.component';
import {ModelFieldSelectComponent} from '../../../../shared/components/inputs/model-field/model-field-select/model-field-select.component';
import {ModelFieldTextComponent} from '../../../../shared/components/inputs/model-field/model-field-text/model-field-text.component';
import {IconPosition} from '../../../../shared/components/inputs/model-field/model-field-number/model-field-number.types';
import {ProviderContractRow} from '../selecthor-admin-provider-list.types';
import {ContractDetailsApplyEvent} from './contract-details-dialog.types';

@Component({
  selector: 'app-contract-details-dialog',
  templateUrl: './contract-details-dialog.component.html',
  imports: [
    BaseDialogComponent,
    ModelFieldPercentageComponent,
    ModelFieldNumberComponent,
    ModelFieldSelectComponent,
    ModelFieldTextComponent,
  ],
})
export class ContractDetailsDialogComponent {
  protected readonly IconPosition = IconPosition;

  private readonly directusService = inject(DirectusService);

  readonly visible = input(false);
  readonly provider = input<ProviderContractRow | null>(null);

  readonly visibleChange = output<boolean>();
  readonly applied = output<ContractDetailsApplyEvent>();

  readonly commissionRate = signal<number | null>(null);
  readonly partnerFeeAmount = signal<number | null>(null);
  readonly initialContractDurationYears = signal<number | null>(null);
  readonly directusProviderFeeFirstDueDateUuid = signal<string | null>(null);
  readonly directusProviderFeeRecurringDueDateUuid = signal<string | null>(null);
  readonly leadRegistrationUrl = signal<string | null>(null);

  readonly firstDueDateOptions = toSignal(
    this.directusService.loadDirectusDropdown('provider_fee_first_due_date'),
    {initialValue: []},
  );
  readonly recurringDueDateOptions = toSignal(
    this.directusService.loadDirectusDropdown('provider_fee_recurring_due_date'),
    {initialValue: []},
  );

  readonly applyDisabled = computed(
    () =>
      this.commissionRate() === null ||
      this.partnerFeeAmount() === null ||
      this.initialContractDurationYears() === null ||
      !this.directusProviderFeeFirstDueDateUuid() ||
      !this.directusProviderFeeRecurringDueDateUuid(),
  );

  constructor() {
    effect(() => this.applyProviderToFields());
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  apply(): void {
    const details: ContractDetailsApplyEvent = {
      commissionRate: this.commissionRate() ?? undefined,
      partnerFeeAmount: this.partnerFeeAmount() ?? undefined,
      initialContractDurationYears: this.initialContractDurationYears() ?? undefined,
      directusProviderFeeFirstDueDateUuid: this.directusProviderFeeFirstDueDateUuid() ?? undefined,
      directusProviderFeeRecurringDueDateUuid:
        this.directusProviderFeeRecurringDueDateUuid() ?? undefined,
      leadRegistrationUrl: this.leadRegistrationUrl() ?? undefined,
    };

    this.applied.emit(details);
  }

  private applyProviderToFields(): void {
    const provider = this.provider();

    this.commissionRate.set(provider?.commissionRate ?? null);
    this.partnerFeeAmount.set(provider?.partnerFeeAmount ?? null);
    this.initialContractDurationYears.set(provider?.initialContractDurationYears ?? null);
    this.directusProviderFeeFirstDueDateUuid.set(
      provider?.directusProviderFeeFirstDueDateUuid ?? null,
    );
    this.directusProviderFeeRecurringDueDateUuid.set(
      provider?.directusProviderFeeRecurringDueDateUuid ?? null,
    );
    this.leadRegistrationUrl.set(provider?.leadRegistrationUrl ?? null);
  }
}
